import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useQuery } from '@apollo/client';

import { createFilterObject } from '@common/utils/createFilterObject';
import { useAutoscroll } from '@common/utils/hooks/useAutoScroll';
import useQueryParams from '@common/utils/hooks/useQueryParams';
import { useScopedTranslation } from '@common/utils/hooks/useScopedTranslation';
import {
  AttributesQuery,
  MeetingTypeLabel,
  MeetingTypesQuery,
  OfficesQuery,
  OfficesQuery_offices as Office,
} from '@typings/api';

import { ATTRIBUTES } from '../../customerForm/queries';
import Section from '../_common/Section';
import SectionHeader from '../_common/SectionHeader';
import { MainFormFields } from '../BookingFlow';
import { MEETING_TYPES, OFFICES } from '../queries';

import { useAddress } from './AddressContext';
import AddressPicker from './AddressPicker';
import Offices from './Offices';
import { Skeleton } from './Skeleton';

const OFFICE_INCREMENT = 3;

export default function OfficesContainer() {
  const { t } = useScopedTranslation();
  const [maxOffices, setMaxOffices] = useState(0);
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const { change } = useForm();
  const { address, addressKey } = useAddress();

  const officesRef = useRef<HTMLDivElement>(null);

  const { data, loading } = useOfficesQuery();
  const offices = useMemo(() => data?.offices ?? [], [data]);
  const visibleOffices = loading ? [] : offices.slice(0, maxOffices);

  const meetingType = useCurrentMeetingType();

  const hasMaxConcurrentTimetableMatch = useMemo(() => {
    if (loading || meetingType == null || address == null) {
      return false;
    }

    return (
      [MeetingTypeLabel.PHONE, MeetingTypeLabel.VIDEO].includes(
        meetingType.label,
      ) && offices.length <= (meetingType?.maxConcurrentTimetables ?? 0)
    );
  }, [address, loading, meetingType, offices]);

  function handleSelectOffice(office: Office) {
    change(MainFormFields.OFFICES, [office]);
  }

  function handleShowMoreOffices() {
    setHasExactMatch(false);
    setMaxOffices(
      maxOffices === 1 ? OFFICE_INCREMENT : maxOffices + OFFICE_INCREMENT,
    );
  }

  useAutoscroll(officesRef);

  useEffect(() => {
    const officesDistanceZero = offices.filter(
      ({ distance }) => distance === 0,
    );

    if (hasMaxConcurrentTimetableMatch) {
      return change(MainFormFields.OFFICES, offices);
    }

    if (officesDistanceZero.length === 1) {
      const exactMatchingOffice = officesDistanceZero[0];

      setHasExactMatch(true);
      setMaxOffices(1);

      return change(MainFormFields.OFFICES, [exactMatchingOffice]);
    }

    if (offices.length === 1) {
      change(MainFormFields.OFFICES, [offices[0]]);
    }

    return setMaxOffices(OFFICE_INCREMENT);
  }, [change, offices, hasMaxConcurrentTimetableMatch]);

  useEffect(() => {
    if (!initialLoadComplete) {
      setInitialLoadComplete(!!data);
    }
  }, [data, initialLoadComplete]);

  useEffect(() => {
    setMaxOffices(0);
    setHasExactMatch(false);
    change(MainFormFields.OFFICES, []);
  }, [change, addressKey]);

  // TODO: remove address here when we work on flow w/o address
  // This was added here to handle the case when the Offices query was skipped due to skip: !meetingType || !address.
  // initialLoadComplete never was true, so the loader was shown infinitely
  if (!initialLoadComplete && address) {
    return <Skeleton ref={officesRef} />;
  }

  if (!meetingType) {
    return <div>{t('errors.technical')}</div>;
  }

  if (hasMaxConcurrentTimetableMatch) {
    return null;
  }

  return (
    <Section ref={officesRef}>
      <SectionHeader>
        {t(`main_form.offices.header.${meetingType.label.toLowerCase()}`)}
      </SectionHeader>
      <div className="flex flex-col space-y-2">
        <AddressPicker
          meetingType={meetingType.label}
          showMap={!loading && offices.length !== 1}
        />
        <Offices
          hasExactMatch={hasExactMatch}
          loading={loading}
          offices={visibleOffices}
          onSelectOffice={handleSelectOffice}
          onShowMoreOffices={
            offices.length === visibleOffices.length
              ? undefined
              : handleShowMoreOffices
          }
        />
      </div>
    </Section>
  );
}

function useOfficesQuery() {
  const { id: contextId } = useQueryParams();
  const {
    values: { address, listingId, meetingType, subjectId },
  } = useFormState();

  const { data: contextData } = useQuery<AttributesQuery>(ATTRIBUTES, {
    variables: { contextId },
    skip: !contextId,
  });

  const { refetch, ...query } = useQuery<OfficesQuery>(OFFICES, {
    variables: {
      coordinates: address?.location,
      location:
        address?.postal_code && address?.country_short
          ? {
              country: address.country_short,
              postalCode: address.postal_code,
            }
          : undefined,
      subjectId,
      meetingType,
      listingId,
      // TODO: re-evaluate
      filters: contextData?.context?.filters?.map(createFilterObject),
    },
    // TODO: Revisit me when we don't want to make address required
    skip: !meetingType || !address,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    // TODO: do we want this to be refetched all the time (see queries in network)
    if (address && refetch) {
      refetch({
        coordinates: address.location,
        location:
          address.postal_code && address.country_short
            ? {
                country: address.country_short,
                postalCode: address.postal_code,
              }
            : undefined,
      });
    }
  }, [refetch, address]);

  return query;
}

function useCurrentMeetingType() {
  const {
    values: { leadSegmentId, listingId, meetingType, subjectId },
  } = useFormState();

  const { data: meetingTypesData } = useQuery<MeetingTypesQuery>(
    MEETING_TYPES,
    {
      variables: {
        subjectId,
        listingId,
        leadSegmentId,
      },
    },
  );

  return useMemo(
    () => meetingTypesData?.meetingTypes?.find((m) => m.label === meetingType),
    [meetingTypesData, meetingType],
  );
}
